import {
  Alert,
  Button,
  Card,
  Chip,
  faDigits,
  Field,
  formatDate,
  formatTime,
  IconButton,
  Panel,
  Seg,
} from "@kitchen/ui";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../api.js";
import { addDays, tehranToday } from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";
import { useMeals } from "../menu/data.js";
import {
  COUNTDOWN_TONE,
  minutesOf,
  type PlanAssignment,
  type PlanDecisions,
  type PlanView,
  TASK_LABEL,
  TRANSFER_LABEL,
  useBoard,
  usePlan,
} from "./data.js";
import { ProductionSettings } from "./settings.js";

type Tab = "plan" | "board" | "settings";
type ComponentSplit = Extract<PlanAssignment, { kind: "component" }>;

const fa = (n: number) => n.toLocaleString("fa-IR");
const time = (t: string) => faDigits(t);

/**
 * F4-T09: the manager's daily kitchen split (design v2 Kitchens.html, architecture §8.5). Approved orders become
 * components; the manager says which kitchen cooks what, when it must be ready and where it is packed; the
 * system computes start and departure times. Nothing reaches a kitchen before «تأیید و ارسال».
 */
export function ProductionPage() {
  const meals = useMeals();
  const [date, setDate] = useState(tehranToday());
  const [mealId, setMealId] = useState("");
  const [tab, setTab] = useState<Tab>("plan");
  const live = (meals.data ?? []).filter((m) => m.active);
  const meal = mealId || live[0]?.id || "";
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "end" }}>
        <Field
          id="prod-date"
          label={formatDate(date)}
          type="date"
          value={date}
          onChange={(e) => {
            if (e.target.value) setDate(e.target.value);
          }}
        />
        <Button
          variant="ghost"
          onClick={() => {
            setDate(addDays(tehranToday(), 1));
          }}
        >
          فردا
        </Button>
        {live.length > 0 && (
          <Seg
            label="وعده"
            value={meal}
            onChange={setMealId}
            options={live.map((m) => ({ value: m.id, label: m.name }))}
          />
        )}
        <span className="grow" />
        <Seg
          label="بخش"
          value={tab}
          onChange={setTab}
          options={[
            { value: "plan", label: "تقسیم کار" },
            { value: "board", label: "تابلوی همه‌ی آشپزخانه‌ها" },
            { value: "settings", label: "تنظیمات تولید" },
          ]}
        />
      </div>
      {tab === "plan" && meal && (
        <PlanEditor
          date={date}
          mealId={meal}
          onSettings={() => {
            setTab("settings");
          }}
        />
      )}
      {tab === "board" && meal && <Overview date={date} mealId={meal} />}
      {tab === "settings" && <ProductionSettings />}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------

function PlanEditor({ date, mealId, onSettings }: { date: string; mealId: string; onSettings: () => void }) {
  const plan = usePlan(date, mealId);
  const v = plan.data;
  const [draft, setDraft] = useState<PlanDecisions | null>(null);
  const key = `${date}|${mealId}|${String(v?.version)}|${String(v?.confirmedAt)}`;
  useEffect(() => {
    setDraft(null);
  }, [key]);
  const d = draft ?? v?.decisions ?? null;
  const refresh = [["production-plan"], ["production-board"]];
  const save = useAction(async (andConfirm: boolean) => {
    if (!d || !v) return;
    let version = v.version ?? 0;
    if (draft || v.version === null) {
      const r = await api<{ version: number }>(`/production/plan/${date}/${mealId}`, {
        method: "PUT",
        body: { ...d, version: v.version ?? 0 },
      });
      version = r.version;
    }
    if (andConfirm) await api(`/production/plan/${date}/${mealId}/confirm`, { body: { version } });
    setDraft(null);
  }, refresh);

  if (!v) return <Panel title="تقسیم کار">در حال بارگذاری…</Panel>;
  if (v.mealTimesMissing || !d) {
    return (
      <Alert
        level="important"
        icon="clock"
        title="زمان‌های این وعده هنوز تعیین نشده است"
        actions={<Button onClick={onSettings}>تعیین زمان‌ها</Button>}
      >
        برای برنامه‌ریزی، «آماده شود تا» و جای بسته‌بندی وعده را در تنظیمات تولید بنویسید.
      </Alert>
    );
  }
  const change = (next: Partial<PlanDecisions>) => {
    setDraft({ ...d, ...next });
  };
  const total = v.foods.reduce((n, f) => n + f.portions, 0);
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: "var(--s3)" }}>
        <Chip tone="blue">{fa(total)} پرس تأییدشده</Chip>
        {v.pendingPortions > 0 && (
          <Chip tone="warn">{fa(v.pendingPortions)} پرس منتظر تأیید (در برنامه نیست)</Chip>
        )}
        {v.confirmedAt ? (
          <Chip tone="ok">
            به آشپزخانه‌ها رفته، {formatTime(v.confirmedAt)}؛ هر تغییر فوراً به آن‌ها می‌رسد
          </Chip>
        ) : (
          <Chip plain>پیش‌نویس؛ تا تأیید نکنید به آشپزخانه‌ها نمی‌رود</Chip>
        )}
        <span className="grow" />
        <Button
          variant="ghost"
          icon="check"
          disabled={!draft || save.busy}
          onClick={() => {
            save.run(false);
          }}
        >
          ذخیره
        </Button>
        <Button
          variant="primary"
          icon="send"
          disabled={save.busy || v.foods.length === 0 || Boolean(v.confirmedAt && !draft)}
          onClick={() => {
            save.run(!v.confirmedAt);
          }}
        >
          {v.confirmedAt ? "ذخیره و ارسال تغییرات" : "تأیید و ارسال به آشپزخانه‌ها"}
        </Button>
      </div>
      <ErrorLine error={save.error} />
      {v.foods.length === 0 && (
        <Alert level="normal" icon="note" title="برای این روز و وعده سفارش تأییدشده‌ای نیست">
          سفارش‌ها بعد از تأیید در «سفارش‌ها» اینجا جمع می‌شوند.
        </Alert>
      )}
      <Habits view={v} decisions={d} onChange={change} />
      <Times view={v} decisions={d} onChange={change} />
      <Foods view={v} decisions={d} onChange={change} />
      <Components view={v} decisions={d} onChange={change} />
      <Warnings view={v} dirty={Boolean(draft)} />
      <Result view={v} dirty={Boolean(draft)} />
    </div>
  );
}

interface Part {
  view: PlanView;
  decisions: PlanDecisions;
  onChange: (next: Partial<PlanDecisions>) => void;
}

const componentSplit = (d: PlanDecisions, componentId: string) =>
  d.assignments.find((a): a is ComponentSplit => a.kind === "component" && a.componentId === componentId);

function Habits({ view, decisions, onChange }: Part) {
  const habits = (view.output?.habits ?? []).filter((h) => {
    const s = componentSplit(decisions, h.componentId);
    return !(s?.splits.length === 1 && s.splits[0]?.kitchenId === h.suggestedKitchenId);
  });
  if (!habits.length) return null;
  const portions = (c: string) => view.components.find((x) => x.componentId === c)?.portions ?? 0;
  const accept = (list: typeof habits) => {
    const ids = new Set(list.map((h) => h.componentId));
    onChange({
      assignments: [
        ...decisions.assignments.filter((a) => !(a.kind === "component" && ids.has(a.componentId))),
        ...list.map((h): ComponentSplit => ({
          kind: "component",
          componentId: h.componentId,
          splits: [{ kitchenId: h.suggestedKitchenId, portions: portions(h.componentId) }],
        })),
      ],
    });
  };
  return (
    <Panel
      title="پیشنهاد بر اساس روزهای گذشته"
      actions={
        <Button
          variant="primary"
          icon="check"
          onClick={() => {
            accept(habits);
          }}
        >
          قبول همه
        </Button>
      }
    >
      <div className="stack">
        <p className="t-cap muted">تا قبول نکنید و برنامه را تأیید نکنید، چیزی به آشپزخانه‌ها نمی‌رود.</p>
        {habits.map((h) => (
          <div key={h.componentId} className="row" style={{ gap: "var(--s3)" }}>
            <span className="grow">{faDigits(h.text)}</span>
            <Button
              variant="ghost"
              onClick={() => {
                accept([h]);
              }}
            >
              قبول
            </Button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Times({ view, decisions, onChange }: Part) {
  const sites = useMemo(() => view.kitchens, [view.kitchens]);
  return (
    <Panel title="زمان‌ها و بسته‌بندی">
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "end" }}>
        <Field
          id="plan-ready"
          label="آماده شود تا"
          type="time"
          value={decisions.readyBy}
          onChange={(e) => {
            if (e.target.value) onChange({ readyBy: e.target.value });
          }}
        />
        <Field
          id="plan-handover"
          label="تحویل به راننده تا"
          type="time"
          value={decisions.handoverBy}
          onChange={(e) => {
            if (e.target.value) onChange({ handoverBy: e.target.value });
          }}
        />
        <div className="field">
          <label htmlFor="plan-pack-site">بسته‌بندی در</label>
          <select
            id="plan-pack-site"
            className="input"
            value={decisions.packing.siteId}
            onChange={(e) => {
              onChange({ packing: { ...decisions.packing, siteId: e.target.value } });
            }}
          >
            {!sites.some((s) => s.siteId === decisions.packing.siteId) && (
              <option value={decisions.packing.siteId}>جای فعلی</option>
            )}
            {sites.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {faDigits(s.name)}
              </option>
            ))}
          </select>
        </div>
        <Field
          id="plan-pack-at"
          label="ساعت بسته‌بندی"
          type="time"
          value={decisions.packing.packAt}
          onChange={(e) => {
            if (e.target.value) onChange({ packing: { ...decisions.packing, packAt: e.target.value } });
          }}
        />
      </div>
      <Field
        id="plan-note"
        label="یادداشت برای آشپزخانه‌ها"
        value={decisions.note}
        onChange={(e) => {
          onChange({ note: e.target.value });
        }}
      />
    </Panel>
  );
}

function Foods({ view, decisions, onChange }: Part) {
  if (!view.foods.length) return null;
  const setFood = (foodItemId: string, kitchenId: string) => {
    const rest = decisions.assignments.filter((a) => !(a.kind === "food" && a.foodItemId === foodItemId));
    onChange({ assignments: kitchenId ? [...rest, { kind: "food", foodItemId, kitchenId }] : rest });
  };
  const setPacking = (foodItemId: string, siteId: string) => {
    const rest = decisions.packingByFood.filter((p) => p.foodItemId !== foodItemId);
    onChange({
      packingByFood: siteId ? [...rest, { foodItemId, siteId, packAt: decisions.packing.packAt }] : rest,
    });
  };
  return (
    <Panel title="غذاها: کل غذا در یک آشپزخانه">
      <div className="stack">
        <p className="t-cap muted">
          برای هر غذا آشپزخانه را انتخاب کنید. اگر یکی از اجزا (مثلاً برنج) را در بخش «اجزا» جدا تقسیم کنید،
          تقسیم جزء بر انتخاب غذا مقدم است.
        </p>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>غذا</th>
                <th>پرس</th>
                <th>اجزا</th>
                <th>آشپزخانه</th>
                <th>بسته‌بندی</th>
              </tr>
            </thead>
            <tbody>
              {view.foods.map((f) => {
                const whole = decisions.assignments.find(
                  (a) => a.kind === "food" && a.foodItemId === f.foodItemId,
                );
                const pack = decisions.packingByFood.find((p) => p.foodItemId === f.foodItemId);
                return (
                  <tr key={f.foodItemId}>
                    <td>{faDigits(f.name)}</td>
                    <td className="num">{fa(f.portions)}</td>
                    <td className="t-cap">{f.components.map((c) => faDigits(c.name)).join("، ")}</td>
                    <td>
                      <select
                        className="input"
                        aria-label={`آشپزخانه‌ی ${f.name}`}
                        value={whole?.kind === "food" ? whole.kitchenId : ""}
                        onChange={(e) => {
                          setFood(f.foodItemId, e.target.value);
                        }}
                      >
                        <option value="">— انتخاب نشده —</option>
                        {view.kitchens.map((k) => (
                          <option key={k.siteId} value={k.siteId}>
                            {faDigits(k.name)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="input"
                        aria-label={`جای بسته‌بندی ${f.name}`}
                        value={pack?.siteId ?? ""}
                        onChange={(e) => {
                          setPacking(f.foodItemId, e.target.value);
                        }}
                      >
                        <option value="">مثل بقیه</option>
                        {view.kitchens.map((k) => (
                          <option key={k.siteId} value={k.siteId}>
                            {faDigits(k.name)}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}

function Components({ view, decisions, onChange }: Part) {
  if (!view.components.length) return null;
  const tasks = view.output?.tasks ?? [];
  const kitchenName = (id: string) => view.kitchens.find((k) => k.siteId === id)?.name ?? "—";
  const setSplit = (componentId: string, splits: ComponentSplit["splits"] | null) => {
    const rest = decisions.assignments.filter(
      (a) => !(a.kind === "component" && a.componentId === componentId),
    );
    onChange({ assignments: splits ? [...rest, { kind: "component", componentId, splits }] : rest });
  };
  const setReady = (componentId: string, readyBy: string) => {
    const rest = decisions.readyByComponent.filter((r) => r.componentId !== componentId);
    onChange({ readyByComponent: readyBy ? [...rest, { componentId, readyBy }] : rest });
  };
  return (
    <Panel title="اجزا: تقسیم بین آشپزخانه‌ها">
      <div className="stack">
        <p className="t-cap muted">
          مثلاً برنج ۲۲۰ = مرکزی ۱۲۰ + تالار ۱۰۰. اگر سفارش‌ها بعداً عوض شوند، آشپزخانه‌ی اول فهرست اختلاف را
          برمی‌دارد و به آشپزخانه‌ها خبر داده می‌شود.
        </p>
        {view.components.map((c) => {
          const split = componentSplit(decisions, c.componentId);
          const now = tasks.filter((t) => t.componentId === c.componentId);
          const sum = split?.splits.reduce((n, s) => n + s.portions, 0) ?? 0;
          const own = decisions.readyByComponent.find((r) => r.componentId === c.componentId);
          return (
            <Card key={c.componentId}>
              <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "center" }}>
                <strong>{faDigits(c.name)}</strong>
                <Chip tone="blue">{fa(c.portions)} پرس</Chip>
                {!split &&
                  (now.length ? (
                    now.map((t) => (
                      <Chip key={t.kitchenId} plain>
                        {faDigits(t.kitchenName)}: {fa(t.portions)}
                      </Chip>
                    ))
                  ) : (
                    <Chip tone="coral">آشپزخانه ندارد</Chip>
                  ))}
                <span className="grow" />
                <Field
                  id={`ready-${c.componentId}`}
                  label="آماده تا (اگر فرق دارد)"
                  type="time"
                  value={own?.readyBy ?? ""}
                  onChange={(e) => {
                    setReady(c.componentId, e.target.value);
                  }}
                />
                {split ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSplit(c.componentId, null);
                    }}
                  >
                    برداشتن تقسیم
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    icon="plus"
                    onClick={() => {
                      const base = now.length
                        ? now.map((t) => ({ kitchenId: t.kitchenId, portions: t.portions }))
                        : [{ kitchenId: view.kitchens[0]?.siteId ?? "", portions: c.portions }];
                      setSplit(c.componentId, base);
                    }}
                  >
                    تقسیم دستی
                  </Button>
                )}
              </div>
              {split && (
                <div className="stack" style={{ marginTop: "var(--s3)" }}>
                  {split.splits.map((s, i) => (
                    <div key={`${s.kitchenId}-${String(i)}`} className="row" style={{ gap: "var(--s2)" }}>
                      <select
                        className="input"
                        aria-label={`آشپزخانه‌ی سهم ${String(i + 1)} ${c.name}`}
                        value={s.kitchenId}
                        onChange={(e) => {
                          setSplit(
                            c.componentId,
                            split.splits.map((x, j) => (j === i ? { ...x, kitchenId: e.target.value } : x)),
                          );
                        }}
                      >
                        {view.kitchens.map((k) => (
                          <option key={k.siteId} value={k.siteId}>
                            {faDigits(k.name)}
                          </option>
                        ))}
                      </select>
                      <input
                        className="input num"
                        style={{ width: 100 }}
                        inputMode="numeric"
                        aria-label={`پرس ${kitchenName(s.kitchenId)} برای ${c.name}`}
                        defaultValue={faDigits(s.portions)}
                        onBlur={(e) => {
                          const n = Number.parseInt(
                            e.target.value.replace(/[۰-۹]/g, (x) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(x))),
                            10,
                          );
                          if (Number.isFinite(n) && n > 0)
                            setSplit(
                              c.componentId,
                              split.splits.map((x, j) => (j === i ? { ...x, portions: n } : x)),
                            );
                        }}
                      />
                      <span>پرس</span>
                      {split.splits.length > 1 && (
                        <IconButton
                          icon="x"
                          label={`حذف سهم ${kitchenName(s.kitchenId)}`}
                          onClick={() => {
                            setSplit(
                              c.componentId,
                              split.splits.filter((_, j) => j !== i),
                            );
                          }}
                        />
                      )}
                    </div>
                  ))}
                  <div className="row" style={{ gap: "var(--s3)" }}>
                    {view.kitchens.length > split.splits.length && (
                      <Button
                        variant="ghost"
                        icon="plus"
                        onClick={() => {
                          const free = view.kitchens.find(
                            (k) => !split.splits.some((s) => s.kitchenId === k.siteId),
                          );
                          if (free)
                            setSplit(c.componentId, [
                              ...split.splits,
                              { kitchenId: free.siteId, portions: Math.max(1, c.portions - sum) },
                            ]);
                        }}
                      >
                        آشپزخانه‌ی دیگر
                      </Button>
                    )}
                    {sum !== c.portions && (
                      <Chip tone="warn">
                        جمع تقسیم {fa(sum)} است، سفارش {fa(c.portions)}؛ اختلاف با آشپزخانه‌ی اول
                      </Chip>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </Panel>
  );
}

function Warnings({ view, dirty }: { view: PlanView; dirty: boolean }) {
  if (dirty) return <Chip plain>برای دیدن نتیجه‌ی تغییرها، ذخیره کنید.</Chip>;
  const list = view.warnings.filter((w) => w.code !== "UNASSIGNED_COMPONENT" || view.foods.length > 0);
  if (!list.length) return null;
  return (
    <div className="stack">
      {list.map((w, i) => (
        <Alert
          key={`${w.code}-${String(i)}`}
          level={w.severity}
          icon={w.severity === "urgent" ? "alert" : "note"}
          title={faDigits(w.message)}
        />
      ))}
    </div>
  );
}

function Result({ view, dirty }: { view: PlanView; dirty: boolean }) {
  const out = view.output;
  if (!out || dirty || (!out.tasks.length && !out.transfers.length)) return null;
  const kitchens = [...new Set(out.tasks.map((t) => t.kitchenId))];
  return (
    <Panel title="نتیجه: کار هر آشپزخانه">
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", alignItems: "start" }}
      >
        {kitchens.map((k) => {
          const list = out.tasks
            .filter((t) => t.kitchenId === k)
            .sort((a, b) => minutesOf(a.startBy) - minutesOf(b.startBy));
          return (
            <Card key={k}>
              <strong>{faDigits(list[0]?.kitchenName ?? "")}</strong>
              <div className="stack" style={{ marginTop: "var(--s2)" }}>
                {list.map((t) => (
                  <div key={t.id} className="row" style={{ gap: "var(--s2)" }}>
                    <span className="grow">
                      {faDigits(t.componentName)} · {fa(t.portions)} پرس
                      {t.batches > 1 ? ` · ${fa(t.batches)} دیگ` : ""}
                    </span>
                    <span className="t-cap muted">
                      شروع {time(t.startBy)} · آماده {time(t.readyBy)}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
      {out.transfers.length > 0 && (
        <div className="stack" style={{ marginTop: "var(--s3)" }}>
          {out.transfers.map((t) => (
            <div key={`${t.componentId}-${t.fromKitchenId}`} className="row" style={{ gap: "var(--s2)" }}>
              <Chip tone="blue">حمل</Chip>
              <span>
                {faDigits(t.componentName)} {fa(t.portions)} پرس از {faDigits(t.fromKitchenName)} به{" "}
                {faDigits(t.toSiteName)}: حرکت {time(t.departBy)}، رسیدن {time(t.arriveBy)} (
                {fa(t.travelMinutes)} دقیقه راه)
              </span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------------------------

/** Every kitchen at a glance with the countdown; the manager can move work that has not started. */
function Overview({ date, mealId }: { date: string; mealId: string }) {
  const board = useBoard(date, mealId, null);
  const plan = usePlan(date, mealId);
  const b = board.data;
  const kitchens = plan.data?.kitchens ?? [];
  const move = useAction(
    (a: { id: string; version: number; to: string }) => {
      const reason = window.prompt("دلیل انتقال کار به آشپزخانه‌ی دیگر؟");
      if (!reason?.trim()) return Promise.resolve();
      return api(`/production/tasks/${a.id}/move`, {
        body: { version: a.version, toKitchenId: a.to, reason },
      });
    },
    [["production-board"], ["production-plan"]],
  );
  if (!b) return <Panel title="تابلوی همه‌ی آشپزخانه‌ها">در حال بارگذاری…</Panel>;
  if (!b.confirmed)
    return (
      <Alert level="normal" icon="note" title="برنامه‌ی این وعده هنوز تأیید نشده است">
        در «تقسیم کار» برنامه را تأیید کنید تا کارها روی تابلوی آشپزخانه‌ها بیاید.
      </Alert>
    );
  const name = (id: string) => kitchens.find((k) => k.siteId === id)?.name ?? "";
  const ids = [...new Set(b.tasks.map((t) => t.kitchenId))];
  return (
    <div className="stack">
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", alignItems: "start" }}
      >
        {ids.map((k) => (
          <Panel key={k} title={faDigits(name(k))}>
            <div className="stack">
              {b.tasks
                .filter((t) => t.kitchenId === k)
                .map((t) => (
                  <Card key={t.id}>
                    <div className="row wrap" style={{ gap: "var(--s2)" }}>
                      <strong className="grow">
                        {faDigits(t.componentName)} · {fa(t.portions)} پرس
                      </strong>
                      <Chip tone={COUNTDOWN_TONE[t.countdown]}>{TASK_LABEL[t.status]}</Chip>
                    </div>
                    <div className="row wrap t-cap muted" style={{ gap: "var(--s2)" }}>
                      <span className="grow">
                        شروع {time(t.startBy)} · آماده {time(t.readyBy)}
                        {t.portionsAtStart !== null && t.portionsAtStart !== t.portions
                          ? ` · بعد از شروع ${fa(t.portionsAtStart)} ← ${fa(t.portions)}`
                          : ""}
                        {t.actualPortions !== null ? ` · تولید واقعی ${fa(t.actualPortions)}` : ""}
                      </span>
                      {t.status === "planned" && kitchens.length > 1 && (
                        <select
                          className="input"
                          aria-label={`انتقال ${t.componentName} به آشپزخانه‌ی دیگر`}
                          value=""
                          onChange={(e) => {
                            if (e.target.value)
                              move.run({ id: t.id, version: t.version, to: e.target.value });
                          }}
                        >
                          <option value="">انتقال به…</option>
                          {kitchens
                            .filter((x) => x.siteId !== k)
                            .map((x) => (
                              <option key={x.siteId} value={x.siteId}>
                                {faDigits(x.name)}
                              </option>
                            ))}
                        </select>
                      )}
                    </div>
                  </Card>
                ))}
            </div>
          </Panel>
        ))}
      </div>
      {b.transfersOut.length > 0 && (
        <Panel title="حمل بین آشپزخانه‌ها">
          <div className="stack">
            {b.transfersOut
              .filter((t) => t.status !== "cancelled")
              .map((t) => (
                <div key={t.id} className="row wrap" style={{ gap: "var(--s2)" }}>
                  <Chip tone={t.status === "received" ? "ok" : t.status === "sent" ? "blue" : "warn"}>
                    {TRANSFER_LABEL[t.status]}
                  </Chip>
                  <span>
                    {fa(t.portions)} پرس از {faDigits(name(t.fromKitchenId))} به{" "}
                    {faDigits(name(t.toSiteId) || "محل بسته‌بندی")} · حرکت {time(t.departBy)}
                    {t.receivedPortions !== null && t.receivedPortions !== t.sentPortions
                      ? ` · مغایرت: ${fa(t.sentPortions ?? 0)} فرستاده، ${fa(t.receivedPortions)} رسید`
                      : ""}
                  </span>
                </div>
              ))}
          </div>
        </Panel>
      )}
      <ErrorLine error={move.error} />
    </div>
  );
}
